import React,{useState,useEffect,useRef} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {useNavigate,useLocation,useParams} from 'react-router-dom';
import {useData} from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import {exportToPDF,shareViaEmail} from '../../../utils/exportUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const {FiArrowLeft,FiArrowRight,FiSend,FiAlertTriangle,FiShield,FiEye,FiEyeOff,FiCheck,FiBuilding,FiX,FiCalendar,FiMapPin,FiGrid,FiList,FiChevronRight,FiChevronLeft,FiLightbulb,FiUsers,FiFileText,FiUpload,FiTrash2,FiInfo,FiPrinter,FiDownload,FiMail,FiActivity,FiCheckCircle,FiLoader}=FiIcons;

const GuidanceTip=({text})=> (
 <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-md mb-2 border border-blue-100">
 <SafeIcon icon={FiLightbulb} className="mt-0.5 flex-shrink-0 text-blue-500" />
 <span className="text-xs leading-relaxed">{text}</span>
 </div>
);

const PortalFooter=({hideBranding})=> {
if (hideBranding) return null;
return (
 <footer className="py-8 text-center opacity-40 hover:opacity-100 transition-opacity mt-4 no-print">
 <div className="flex items-center justify-center gap-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">
 <SafeIcon icon={FiShield} size={10} />
 <span>Sikker varslingsportal levert av</span>
 <a href="https://hmsvarsling.no" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors underline underline-offset-2"> HMSVarsling.no </a>
 </div>
 </footer>
);
};

const WhistleblowerForm=()=> {
const navigate=useNavigate();
const {companyId}=useParams();
const {companies,addReport}=useData();
const {validateSubmission,HoneypotField} = useFormSecurity('whistleblower_form');
const company=companies.find(c=> c.id===companyId);
const fileInputRef=useRef(null);
const reportRef=useRef(null);
const [currentStep,setCurrentStep]=useState(1);
const [viewMode,setViewMode]=useState('grid');
const [isSubmitting,setIsSubmitting]=useState(false);
const [isGenerating,setIsGenerating]=useState(false);
const [showValidationErrors,setShowValidationErrors]=useState(false);
const [submitError,setSubmitError]=useState(null);

const [formData,setFormData]=useState({
companyId: companyId,
type: '',
category: '',
title: '',
whatHappened: '',
whenHappened: '',
whereHappened: '',
severity: 'medium',
isAnonymous: false,
reporterName: '',
reporterEmail: '',
reporterPhone: '',
involvedPersons: '',
witnesses: '',
evidenceDescription: '',
files: [],
notificationsEnabled: false,
notificationEmail: ''
});

const safeCategories = {
  mobbing: {
    label: '1. MOBBING, TRAKASSERING OG DISKRIMINERING',
    color: '#7C3AED',
    categories: [
      { id: 'mobbing', name: 'Mobbing', description: 'Gjentatte negative eller krenkende handlinger over tid.' },
      { id: 'utfrysing', name: 'Utfrysing eller utestenging', description: 'Bevisst utestenging fra fellesskap, informasjon eller arbeidsrelaterte aktiviteter.' },
      { id: 'seksuell_trakassering', name: 'Seksuell trakassering', description: 'Uønsket seksuell oppmerksomhet, kommentarer, meldinger, berøring eller adferd.' },
      { id: 'annen_trakassering', name: 'Annen trakassering', description: 'Krenkende, skremmende, fiendtlig eller nedverdigende adferd.' },
      { id: 'diskriminering', name: 'Diskriminering', description: 'Usaklig forskjellsbehandling knyttet til for eksempel kjønn, alder, etnisitet, religion eller funksjonsnedsettelse.' },
      { id: 'krenkende_adferd', name: 'Krenkende eller nedverdigende adferd', description: 'Utskjelling, ydmykelse eller andre alvorlige krenkelser.' },
      { id: 'gjentatt_uonsket', name: 'Gjentatt uønsket adferd', description: 'Uønskede handlinger som fortsetter over tid.' }
    ]
  },
  arbeidsmiljo: {
    label: '2. KRITIKKVERDIGE FORHOLD I ARBEIDSMILJØET',
    color: '#2563EB',
    categories: [
      { id: 'fryktkultur', name: 'Fryktkultur eller taushetskultur', description: 'Ansatte opplever at det kan få negative konsekvenser å si fra.' },
      { id: 'utilborlig_press', name: 'Utilbørlig press', description: 'Press til å gjøre, godta eller unnlate noe på en kritikkverdig måte.' },
      { id: 'uforsvarlig_belastning', name: 'Uforsvarlig arbeidsbelastning', description: 'Alvorlig eller vedvarende belastning som kan påvirke helse eller sikkerhet.' },
      { id: 'manglende_ivaretakelse', name: 'Manglende ivaretakelse av ansatte', description: 'Alvorlige bekymringer eller problemer blir ikke fulgt opp.' },
      { id: 'organisatoriske_forhold', name: 'Kritikkverdige organisatoriske forhold', description: 'Organisering, bemanning eller praksis skaper alvorlige problemer.' },
      { id: 'ignorert_miljo', name: 'Alvorlige arbeidsmiljøproblemer blir ignorert', description: 'Kjente problemer får fortsette uten nødvendige tiltak.' },
      { id: 'lederforhold', name: 'Kritikkverdige lederforhold', description: 'Alvorlig eller systematisk lederadferd som kan være kritikkverdig.' }
    ]
  },
  vold: {
    label: '3. VOLD, TRUSLER OG ALVORLIG GRENSEOVERSKRIDENDE ADFERD',
    color: '#DC2626',
    categories: [
      { id: 'fysisk_vold', name: 'Fysisk vold', description: 'Slag, dytting, spark eller andre fysiske angrep.' },
      { id: 'trusler', name: 'Trusler', description: 'Trusler om vold, skade eller andre alvorlige konsekvenser.' },
      { id: 'truende_adferd', name: 'Truende eller aggressiv adferd', description: 'Adferd som skaper alvorlig frykt eller utrygghet.' },
      { id: 'seksuelle_krenkelser', name: 'Seksuelle krenkelser', description: 'Alvorlig seksuelt grenseoverskridende adferd.' },
      { id: 'stalking', name: 'Forfølgelse eller stalking', description: 'Gjentatt uønsket kontakt, overvåking eller forfølgelse.' },
      { id: 'tvang', name: 'Press, tvang eller skremmende adferd', description: 'Forsøk på å påvirke andre gjennom press, tvang eller frykt.' }
    ]
  },
  maktmisbruk: {
    label: '4. MISBRUK AV MAKT, STILLING ELLER MYNDIGHET',
    color: '#D97706',
    categories: [
      { id: 'misbruk_lederposisjon', name: 'Misbruk av lederposisjon', description: 'Lederrollen brukes på en alvorlig kritikkverdig måte.' },
      { id: 'myndighetsmisbruk', name: 'Myndighetsmisbruk', description: 'Fullmakter eller beslutningsmyndighet brukes uriktig.' },
      { id: 'press_tvang', name: 'Utilbørlig press eller tvang', description: 'En maktposisjon brukes til å presse andre.' },
      { id: 'usaklig_forskjell', name: 'Usaklig forskjellsbehandling', description: 'Personer behandles ulikt uten saklig grunn.' },
      { id: 'beslutningsmyndighet', name: 'Misbruk av beslutningsmyndighet', description: 'Beslutninger påvirkes av personlige eller uvedkommende hensyn.' },
      { id: 'trusler_negativt', name: 'Trusler om negative konsekvenser', description: 'Posisjon eller myndighet brukes for å true noen til å innrette seg.' }
    ]
  },
  lovbrudd: {
    label: '5. BRUDD PÅ LOVER, FORSKRIFTER ELLER MYNDIGHETSKRAV',
    color: '#0891B2',
    categories: [
      { id: 'mulig_lovbrudd', name: 'Mulig lovbrudd', description: 'Mistanke om handlinger eller praksis som kan være ulovlig.' },
      { id: 'brudd_aml', name: 'Brudd på arbeidsmiljøregelverket', description: 'Alvorlige eller systematiske brudd på arbeidstakeres rettigheter.' },
      { id: 'brudd_hms', name: 'Brudd på HMS-regelverket', description: 'HMS- eller sikkerhetskrav brytes eller omgås.' },
      { id: 'brudd_personvern', name: 'Brudd på personvernreglene', description: 'Personopplysninger behandles på en mulig ulovlig måte.' },
      { id: 'brudd_arbeidstid', name: 'Brudd på arbeidstidsbestemmelser', description: 'Alvorlige eller systematiske brudd på arbeidstidsreglene.' },
      { id: 'manglende_etterlevelse', name: 'Manglende etterlevelse av myndighetspålegg', description: 'Pålegg eller krav fra myndighetene ignoreres eller omgås.' },
      { id: 'feilrapportering', name: 'Bevisst feilrapportering til myndigheter', description: 'Uriktige eller mangelfulle opplysninger gis med hensikt.' }
    ]
  },
  okonomisk: {
    label: '6. ØKONOMISKE MISLIGHETER, KORRUPSJON OG KRIMINALITET',
    color: '#B45309',
    categories: [
      { id: 'korrupsjon', name: 'Korrupsjon', description: 'Misbruk av stilling eller posisjon for å oppnå utilbørlige fordeler.' },
      { id: 'bestikkelser', name: 'Bestikkelser', description: 'Penger, gaver eller fordeler brukes for å påvirke beslutninger.' },
      { id: 'underslag', name: 'Underslag', description: 'Penger eller verdier som tilhører andre tilegnes urettmessig.' },
      { id: 'bedrageri', name: 'Bedrageri eller svindel', description: 'Bevisst villedning for å oppnå økonomisk eller annen fordel.' },
      { id: 'tyveri', name: 'Tyveri', description: 'Urettmessig tilegnelse av virksomhetens eller andres eiendeler.' },
      { id: 'regnskap', name: 'Regnskapsmanipulasjon', description: 'Regnskap, bilag eller økonomiske opplysninger manipuleres eller skjules.' },
      { id: 'utbetalinger', name: 'Urettmessige utbetalinger', description: 'Betalinger eller refusjoner foretas uten legitimt grunnlag.' },
      { id: 'misbruk_midler', name: 'Misbruk av virksomhetens midler', description: 'Penger, eiendeler eller ressurser brukes urettmessig.' },
      { id: 'hvitvasking', name: 'Hvitvasking', description: 'Mistanke om at opprinnelsen til ulovlige midler forsøkes skjult.' },
      { id: 'skatteunndragelse', name: 'Skatte- eller avgiftsunndragelse', description: 'Mistanke om bevisst unndragelse eller feilrapportering.' },
      { id: 'forfalskning', name: 'Dokumentforfalskning', description: 'Dokumenter, bilag, signaturer eller opplysninger forfalskes.' }
    ]
  },
  habilitet: {
    label: '7. INTERESSEKONFLIKT, HABILITET OG FAVORISERING',
    color: '#9333EA',
    categories: [
      { id: 'interessekonflikt', name: 'Interessekonflikt', description: 'Private eller økonomiske interesser kan påvirke beslutninger.' },
      { id: 'habilitetsbrudd', name: 'Habilitetsbrudd', description: 'En person deltar i en beslutning til tross for egne interesser eller bindinger.' },
      { id: 'favorisering_nærst', name: 'Favorisering av familie eller venner', description: 'Nærstående gis urimelige fordeler.' },
      { id: 'favorisering_ekstern', name: 'Favorisering av leverandør eller samarbeidspartner', description: 'En ekstern aktør favoriseres uten saklig grunn.' },
      { id: 'inhabilitet_ansettelse', name: 'Inhabilitet ved ansettelse', description: 'Personlige relasjoner eller interesser påvirker en ansettelse.' },
      { id: 'inhabilitet_kontrakt', name: 'Inhabilitet ved innkjøp eller kontrakter', description: 'Personlige interesser påvirker leverandørvalg eller avtaler.' },
      { id: 'skjulte_interesser', name: 'Skjulte økonomiske interesser eller bindinger', description: 'Relevante eierinteresser, relasjoner eller verv holdes skjult.' },
      { id: 'uakseptable_gaver', name: 'Uakseptable gaver eller fordeler', description: 'Gaver eller fordeler kan påvirke beslutninger på en utilbørlig måte.' }
    ]
  },
  personvern: {
    label: '8. PERSONVERN, INFORMASJON OG KONFIDENSIALITET',
    color: '#4F46E5',
    categories: [
      { id: 'innsyn_personinfo', name: 'Urettmessig innsyn i personopplysninger', description: 'Noen åpner eller bruker personopplysninger uten legitimt behov.' },
      { id: 'uautorisert_tilgang', name: 'Uautorisert tilgang til informasjon', description: 'Informasjon eller systemer åpnes uten nødvendig tillatelse.' },
      { id: 'ulovlig_deling', name: 'Ulovlig deling av personopplysninger', description: 'Personopplysninger deles med uvedkommende.' },
      { id: 'deling_konfidensielt', name: 'Deling av konfidensiell informasjon', description: 'Fortrolige eller taushetsbelagte opplysninger deles urettmessig.' },
      { id: 'misbruk_system', name: 'Misbruk av systemtilganger', description: 'Tilgang til systemer eller data brukes på en kritikkverdig måte.' },
      { id: 'ulovlig_overvåking', name: 'Ulovlig eller kritikkverdig overvåking', description: 'Ansatte eller andre overvåkes uten tilstrekkelig grunnlag.' },
      { id: 'manipulering_info', name: 'Manipulering, sletting eller skjuling av informasjon', description: 'Informasjon endres eller fjernes for å skjule et forhold.' }
    ]
  },
  fare: {
    label: '9. ALVORLIG FARE FOR LIV, HELSE ELLER SIKKERHET',
    color: '#E11D48',
    categories: [
      { id: 'ignorert_fare', name: 'Kjent alvorlig fare blir ignorert', description: 'Virksomheten kjenner til en alvorlig risiko, men nødvendige tiltak uteblir.' },
      { id: 'omgått_sikkerhet', name: 'Sikkerhetskrav omgås bevisst', description: 'Viktige sikkerhetsregler eller tiltak settes bevisst til side.' },
      { id: 'skjulte_mangler', name: 'Alvorlige sikkerhetsmangler skjules', description: 'Informasjon om alvorlig risiko holdes tilbake.' },
      { id: 'ignorert_varsel_fare', name: 'Varsler om alvorlig fare blir ignorert', description: 'Tidligere meldinger om alvorlig risiko blir ikke fulgt opp.' },
      { id: 'press_farlig_arbeid', name: 'Press til å arbeide uforsvarlig', description: 'Ansatte presses til arbeid som kan innebære alvorlig fare.' },
      { id: 'manglende_handling', name: 'Manglende handling på kjent risiko', description: 'Ansvarlige personer unnlater å håndtere en kjent alvorlig risiko.' }
    ]
  },
  miljo: {
    label: '10. KRITIKKVERDIGE KLIMA- ELLER MILJØFORHOLD',
    color: '#059669',
    categories: [
      { id: 'ulovlig_utslipp', name: 'Ulovlig utslipp eller forurensning', description: 'Mistanke om ulovlig utslipp til luft, vann eller grunn.' },
      { id: 'avfallshåndtering', name: 'Ulovlig håndtering av avfall', description: 'Avfall eller farlige stoffer håndteres i strid med regelverket.' },
      { id: 'skjult_miljoskade', name: 'Miljøskade blir skjult', description: 'Forurensning eller miljøskade holdes bevisst tilbake.' },
      { id: 'brudd_miljoregelverk', name: 'Brudd på miljøregelverket', description: 'Miljøkrav brytes eller omgås.' },
      { id: 'manipulering_miljodata', name: 'Manipulering av miljødata', description: 'Målinger eller miljørapportering endres eller fremstilles uriktig.' },
      { id: 'ignorert_miljorisiko', name: 'Alvorlig miljørisiko blir ignorert', description: 'Kjent risiko for betydelig miljøskade håndteres ikke.' }
    ]
  },
  uetisk: {
    label: '11. UETISK ADFERD ELLER BRUDD PÅ ETISKE RETNINGSLINJER',
    color: '#0F766E',
    categories: [
      { id: 'brudd_etiske_retn', name: 'Brudd på virksomhetens etiske retningslinjer', description: 'Alvorlig adferd i strid med virksomhetens skriftlige etiske regler.' },
      { id: 'villedende_adferd', name: 'Uredelig eller villedende adferd', description: 'Bevisst feilinformasjon eller annen alvorlig uredelig opptreden.' },
      { id: 'brudd_profesjonell', name: 'Alvorlig brudd på profesjonelle standarder', description: 'Faglige eller profesjonelle krav brytes på en alvorlig måte.' },
      { id: 'skjuling_kritikkverdig', name: 'Bevisst skjuling av kritikkverdige forhold', description: 'Noen forsøker å dekke over alvorlige forhold.' },
      { id: 'annen_uetisk', name: 'Annen alvorlig uetisk adferd', description: 'Alvorlig adferd som strider mot allment aksepterte etiske normer.' }
    ]
  },
  gjengjeldelse: {
    label: '12. GJENGJELDELSE ETTER VARSLING',
    color: '#BE123C',
    categories: [
      { id: 'trusler_etter_varsling', name: 'Trusler eller press etter varsling', description: 'Negative reaksjoner eller press etter at noen har sagt fra.' },
      { id: 'trakassering_etter_varsling', name: 'Utfrysing eller trakassering etter varsling', description: 'Negativ behandling fra leder eller kolleger etter varsling.' },
      { id: 'negative_endringer', name: 'Negative endringer i arbeidsoppgaver eller ansvar', description: 'Rolle eller oppgaver endres negativt etter varsling.' },
      { id: 'omplassering', name: 'Omplassering eller andre negative tiltak', description: 'Negative tiltak iverksettes etter at noen har sagt fra.' },
      { id: 'oppsigelse_trussel', name: 'Oppsigelse eller trussel om oppsigelse', description: 'Varsling etterfølges av oppsigelse eller truser om dette.' },
      { id: 'tap_karriere', name: 'Tap av karriere- eller utviklingsmuligheter', description: 'Varslingen får negative konsekvenser for muligheter på arbeidsplassen.' },
      { id: 'press_trekke_varsel', name: 'Press om å trekke varselet', description: 'Noen forsøker å få varsleren til å trekke eller endre varselet.' }
    ]
  },
  hindring: {
    label: '13. HINDRING ELLER KRITIKKVERDIG HÅNDTERING OF VARSLING',
    color: '#C2410C',
    categories: [
      { id: 'hindre_varsling', name: 'Forsøk på å hindre varsling', description: 'Noen forsøker å stoppe en person fra å varsle.' },
      { id: 'ignorert_varsel', name: 'Varsel blir bevisst ignorert', description: 'Et alvorlig varsel mottas, men blir ikke håndtert.' },
      { id: 'dekke_over', name: 'Forsøk på å dekke over saken', description: 'Noen forsøker å skjule forholdet eller hindre undersøkelse.' },
      { id: 'fjerne_bevis', name: 'Dokumentasjon eller bevis blir fjernet', description: 'Relevant materiale slettes, endres eller skjules.' },
      { id: 'press_vitner', name: 'Vitner blir presset eller påvirket', description: 'Personer forsøkes påvirket til å endre eller holde tilbake opplysninger.' },
      { id: 'brudd_konfidensialitet', name: 'Brudd på konfidensialitet i varslingssaken', description: 'Opplysninger om varsler eller sak deles med uvedkommende.' },
      { id: 'behandling_tidligere_varsel', name: 'Kritikkverdig behandling av tidligere varsel', description: 'Bekymring knyttet til hvordan en tidligere varslingssak er håndtert.' }
    ]
  },
  andre: {
    label: '14. ANDRE KRITIKKVERDIGE FORHOLD',
    color: '#64748B',
    categories: [
      { id: 'brudd_interne_regler', name: 'Alvorlig brudd på interne regler eller retningslinjer', description: 'Vesentlige interne krav brytes eller omgås.' },
      { id: 'behandling_kunder', name: 'Kritikkverdig behandling av kunder, brukere eller andre', description: 'Andre personer behandles på en alvorlig uforsvarlig eller uetisk måte.' },
      { id: 'annet_kritikkverdig', name: 'Annet mulig kritikkverdig forhold', description: 'Et alvorlig forhold som ikke passer i kategoriene over.' }
    ]
  },
  apen: {
    label: '15. ÅPEN VARSLING',
    color: '#2563EB',
    categories: [
      { id: 'apen_beskrivelse', name: 'Jeg finner ikke riktig kategori / ønsker å beskrive saken selv', description: 'Velg dette dersom du er usikker eller ingen av kategoriene passer.' }
    ]
  }
};

const handleInputChange=(field,value)=> {
setFormData(prev=> ({...prev,[field]: value}));
};

const handleCategorySelect=(categoryId,sectionLabel)=> {
setFormData(prev=> ({...prev,type: categoryId,category: sectionLabel}));
setShowValidationErrors(false);
};

const validateStep=(step)=> {
if (step===1) return !!formData.type;
if (step===2) return formData.title && formData.whatHappened && formData.whenHappened && formData.whereHappened;
if (step===5) return formData.isAnonymous || (formData.reporterName && formData.reporterEmail);
return true;
};

const handleNext=()=> {
if (validateStep(currentStep)) {
setCurrentStep(prev=> prev + 1);
setShowValidationErrors(false);
window.scrollTo(0,0);
} else {
setShowValidationErrors(true);
}
};

const handleSubmit=async (e)=> {
if (e) e.preventDefault();
setSubmitError(null);
if (!validateSubmission()) {
return;
}
setIsSubmitting(true);
try {
const reportData={
  ...formData,
  description: formData.whatHappened,
  location: formData.whereHappened,
  incidentDate: formData.whenHappened,
  reportType: 'whistle_report',
  status: 'new',
  notification_email: formData.notificationsEnabled ? formData.notificationEmail : null,
  notifications_enabled: formData.notificationsEnabled
};
const result=await addReport(reportData);
if (result && result.success && result.data?.id) {
navigate('/portal/confirmation',{state: {reportId: result.data.id,isAnonymous: formData.isAnonymous}});
} else {
const msg=result?.error || "Mangler ID fra database";
setSubmitError(msg);
setIsSubmitting(false);
}
} catch (error) {
setSubmitError(error.message || "Uventet feil i skjema-prosessen");
setIsSubmitting(false);
}
};

const handleDownloadPDF=async ()=> {
setIsGenerating(true);
try {
  await exportToPDF(
    {...formData, description: formData.whatHappened, companyName: company?.name},
    'whistle_report',
    reportRef.current
  );
} catch (error) {
  console.error("PDF feilet:",error);
} finally {
  setIsGenerating(false);
}
};

const handleShareEmail=()=> {
shareViaEmail({...formData,description: formData.whatHappened},'whistle_report');
};

const getInputClass=(fieldName)=> {
const hasError=showValidationErrors && !formData[fieldName];
return `w-full p-4 bg-white border-2 rounded-xl text-sm outline-none transition-all ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500'}`;
};

const handleFileUploadTrigger=()=> {
fileInputRef.current?.click();
};

const handleFileChange=(e)=> {
const selectedFiles=Array.from(e.target.files);
const formattedFiles=selectedFiles.map(file=> ({name: file.name,size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',type: file.type,lastModified: file.lastModified,fileObject: file}));
setFormData(prev=> ({...prev,files: [...prev.files,...formattedFiles]}));
e.target.value='';
};

const removeFile=(index)=> {
setFormData(prev=> ({...prev,files: prev.files.filter((_,i)=> i !==index)}));
};

const PreviewField=({label,value,fullWidth=false})=> (
 <div className={`${fullWidth ? 'md:col-span-2' : ''} border-b border-gray-100 pb-3`}>
 <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
 <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">{value || '-'}</p>
 </div>
);

return (
 <div className="min-h-screen bg-[#0F172A] py-8 font-sans flex flex-col">
 <div className="max-w-4xl mx-auto px-4 w-full flex-1">
 <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-6 mb-6 no-print">
 <div className="flex items-center justify-between mb-8">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white text-2xl shadow-lg">
 <SafeIcon icon={FiShield} />
 </div>
 <div>
 <h1 className="text-xl font-black text-gray-900 uppercase tracking-tight">Varslingsskjema</h1>
 <p className="text-sm text-gray-500 font-medium">{company?.name || 'Laster bedrift...'}</p>
 </div>
 </div>
 <button onClick={()=> navigate(-1)} className="text-gray-400 hover:text-gray-600" type="button">
 <SafeIcon icon={FiX} size={24} />
 </button> 
 </div>
 <div className="flex items-center justify-between relative">
 {[1,2,3,4,5,6].map((step)=> (
 <div key={step} className="flex flex-col items-center gap-2 bg-white px-2 relative z-10">
 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${currentStep===step ? 'bg-gray-900 text-white scale-110 shadow-lg' : currentStep > step ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
 {currentStep > step ? <SafeIcon icon={FiCheck} /> : step}
 </div>
 </div>
 ))}
 <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-100 -z-0"></div>
 </div>
 </div>

 {submitError && (
 <div className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-start gap-3">
 <SafeIcon icon={FiAlertTriangle} className="mt-1" />
 <div>
 <p className="font-bold">FEIL VED INNSENDING:</p>
 <p className="text-sm">{submitError}</p>
 </div>
 </div>
 )}

 <form onSubmit={handleSubmit} className="bg-white rounded-[32px] shadow-xl border border-gray-200 overflow-hidden min-h-[600px] flex flex-col">
 <HoneypotField />
 <div className={`flex-1 ${currentStep === 1 ? 'bg-[#F6F8FB]' : 'p-8'}`}>
 <AnimatePresence mode="wait">
 {currentStep===1 && (
 <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} className="p-8 space-y-6">
 <div className="text-center mb-8">
 <h2 className="text-2xl font-black text-gray-900">Hva gjelder saken?</h2>
 <div className="flex justify-center gap-2 mt-4 bg-gray-200 p-1 rounded-xl w-fit mx-auto">
 <button type="button" onClick={()=> setViewMode('grid')} className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 ${viewMode==='grid' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}><SafeIcon icon={FiGrid} /> Rutenett</button>
 <button type="button" onClick={()=> setViewMode('list')} className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 ${viewMode==='list' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}><SafeIcon icon={FiList} /> Liste</button>
 </div>
 </div>

 <div className="space-y-6">
 {Object.entries(safeCategories).map(([key,section])=> (
 <div key={key} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
 <h3 
   className="text-sm font-semibold uppercase tracking-wider mb-6 pl-3 border-l-4 text-[#0F172A]"
   style={{ borderLeftColor: section.color }}
 >
   {section.label}
 </h3>
 <div className={viewMode==='grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>
 {section.categories.map(cat=> {
   const isSelected = formData.type === cat.id;
   return (
 <button 
   key={cat.id} 
   type="button" 
   onClick={()=> handleCategorySelect(cat.id,section.label)} 
   className={`text-left p-5 rounded-xl border transition-all relative group cursor-pointer ${isSelected ? 'bg-blue-50/50 border-blue-600 ring-1 ring-blue-100' : 'bg-[#F8FAFC] border-[#DCE3EC] hover:bg-white hover:border-blue-300 hover:shadow-[0_2px_6px_rgba(15,23,42,0.06)]'}`}
 >
 <div className="flex justify-between items-start">
 <div className="pr-4">
 <span className={`font-bold text-[15px] block mb-1 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{cat.name}</span>
 <span className="text-xs text-[#64748B] leading-relaxed block">{cat.description}</span>
 </div>
 <div className="mt-1 flex-shrink-0">
   {isSelected ? (
     <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white">
       <SafeIcon icon={FiCheck} size={12} />
     </div>
   ) : (
     <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-blue-500 transition-transform group-hover:translate-x-1" />
   )}
 </div>
 </div>
 </button>
   )})}
 </div>
 </div>
 ))}
 </div>
 </motion.div>
 )}

 {currentStep===2 && (
 <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} className="space-y-6">
 <h2 className="text-2xl font-black text-gray-900 text-center">Beskrivelse av forholdet</h2>
 <div className="space-y-6">
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Tittel på saken *</label>
 <input type="text" value={formData.title} onChange={e=> handleInputChange('title',e.target.value)} className={getInputClass('title')} placeholder="Kort beskrivende tittel..." />
 </div>
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Hva skjedde? *</label>
 <GuidanceTip text="Beskriv hendelsen så objektivt som mulig. Inkluder fakta,hvem som var involvert og ev. vitner." />
 <textarea rows={6} value={formData.whatHappened} onChange={e=> handleInputChange('whatHappened',e.target.value)} className={getInputClass('whatHappened')} placeholder="Beskriv detaljert..." />
 </div>
 <div className="grid md:grid-cols-2 gap-6">
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Tidspunkt *</label>
 <input type="datetime-local" value={formData.whenHappened} onChange={e=> handleInputChange('whenHappened',e.target.value)} className={getInputClass('whenHappened')} />
 </div>
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Sted *</label>
 <input type="text" value={formData.whereHappened} onChange={e=> handleInputChange('whereHappened',e.target.value)} placeholder="F.eks. Avdeling B,Kantinen" className={getInputClass('whereHappened')} />
 </div>
 </div>
 </div>
 </motion.div>
 )}

 {currentStep===3 && (
 <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} className="space-y-6">
 <h2 className="text-2xl font-black text-gray-900 text-center">Involverte personer</h2>
 <div className="space-y-6">
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2 flex items-center gap-2"><SafeIcon icon={FiUsers} /> Hvem gjelder saken?</label>
 <GuidanceTip text="Navn på personer som er involvert eller som varselet retter seg mot." />
 <textarea rows={3} value={formData.involvedPersons} onChange={e=> handleInputChange('involvedPersons',e.target.value)} className={formData.involvedPersons ? 'w-full p-4 bg-white border-2 border-gray-200 rounded-xl text-sm' : getInputClass('involvedPersons')} placeholder="Navn,rolle..." />
 </div>
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2 flex items-center gap-2"><SafeIcon icon={FiEye} /> Evt. Vitner</label>
 <GuidanceTip text="Var det andre tilstede som observerte hendelsen?" />
 <textarea rows={3} value={formData.witnesses} onChange={e=> handleInputChange('witnesses',e.target.value)} className={formData.witnesses ? 'w-full p-4 bg-white border-2 border-gray-200 rounded-xl text-sm' : getInputClass('witnesses')} placeholder="Navn på vitner..." />
 </div>
 </div>
 </motion.div>
 )}

 {currentStep===4 && (
 <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} className="space-y-6">
 <h2 className="text-2xl font-black text-gray-900 text-center">Dokumentasjon</h2>
 <div className="space-y-6">
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-2 flex items-center gap-2"><SafeIcon icon={FiFileText} /> Beskrivelse av bevis</label>
 <textarea rows={4} value={formData.evidenceDescription} onChange={e=> handleInputChange('evidenceDescription',e.target.value)} className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl text-sm outline-none" placeholder="Har du e-porter,meldinger,bilder eller annet som underbygger saken?" />
 </div>
 <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
 <label className="block text-xs font-black text-gray-500 uppercase mb-4 flex items-center gap-2"><SafeIcon icon={FiUpload} /> Last opp vedlegg</label>
 <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" multiple />
 <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-white hover:border-blue-400 transition-colors cursor-pointer" onClick={handleFileUploadTrigger}>
 <SafeIcon icon={FiUpload} className="text-3xl text-gray-400 mx-auto mb-2" />
 <p className="text-sm text-gray-600 font-bold">Klikk for å laste opp filer</p>
 <p className="text-xs text-gray-400 mt-1">PDF,JPG,PNG (Maks 10MB)</p>
 </div>
 {formData.files.length > 0 && (
 <div className="mt-4 space-y-2">
 {formData.files.map((file,index)=> (
 <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
 <div className="flex items-center gap-3">
 <SafeIcon icon={FiFileText} className="text-blue-500" />
 <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">{file.name}</span>
 <span className="text-xs text-gray-400">({file.size})</span>
 </div>
 <button type="button" onClick={()=> removeFile(index)} className="text-red-400 hover:text-red-600 p-1">
 <SafeIcon icon={FiTrash2} />
 </button>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </motion.div>
 )}

 {currentStep===5 && (
 <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} className="space-y-6">
 <div className="text-center mb-8">
 <h2 className="text-2xl font-black text-gray-900">Kontaktinformasjon</h2>
 <p className="text-gray-500 text-sm mt-2">Du kan velge å være anonym.</p>
 </div>
 <div className="bg-gray-50 p-8 rounded-3xl border border-gray-200">
 <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
 <div className="flex items-center gap-4">
 <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg ${formData.isAnonymous ? 'bg-blue-600' : 'bg-gray-300'}`}>
 <SafeIcon icon={formData.isAnonymous ? FiEyeOff : FiEye} />
 </div>
 <div>
 <h3 className="font-bold text-gray-900">Anonym varsling</h3>
 <p className="text-xs text-gray-500">Din identitet skjules for arbeidsgiver</p>
 </div>
 </div>
 <label className="relative inline-flex items-center cursor-pointer">
 <input type="checkbox" checked={formData.isAnonymous} onChange={e=> handleInputChange('isAnonymous',e.target.checked)} className="sr-only peer" />
 <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
 </label>
 </div>
 {!formData.isAnonymous ? (
 <div className="space-y-4 animate-in fade-in slide-in-from-top-4">
 <div>
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Fullt navn *</label>
 <input type="text" value={formData.reporterName} onChange={e=> handleInputChange('reporterName',e.target.value)} className={getInputClass('reporterName')} placeholder="Ditt navn" />
 </div>
 <div className="grid md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">E-post *</label>
 <input type="email" value={formData.reporterEmail} onChange={e=> handleInputChange('reporterEmail',e.target.value)} className={getInputClass('reporterEmail')} placeholder="din@epost.no" />
 </div>
 <div>
 <label className="block text-xs font-black text-gray-500 uppercase mb-2">Telefon</label>
 <input type="tel" value={formData.reporterPhone} onChange={e=> handleInputChange('reporterPhone',e.target.value)} className={getInputClass('reporterPhone')} placeholder="900 00 000" />
 </div>
 </div>
 </div>
 ) : (
 <div className="space-y-6">
   <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3 text-sm text-blue-800">
    <SafeIcon icon={FiInfo} className="text-xl mt-0.5 flex-shrink-0" />
    <p>Selv om du varsler anonymt,vil du få en tilgangskode slik at du kan logge inn og kommunisere trygt med saksbehandler via vår krypterte chat.</p>
   </div>

   <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
     <div className="flex items-center justify-between mb-4">
       <div className="flex items-center gap-3">
         <SafeIcon icon={FiMail} className="text-blue-600" />
         <h4 className="font-bold text-gray-900">Anonym statusvarsling</h4>
       </div>
       <label className="relative inline-flex items-center cursor-pointer">
         <input type="checkbox" checked={formData.notificationsEnabled} onChange={e=> handleInputChange('notificationsEnabled',e.target.checked)} className="sr-only peer" />
         <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
       </label>
     </div>
     
     {formData.notificationsEnabled && (
       <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
         <p className="text-xs text-gray-500 leading-relaxed">
           Oppgi en e-postadresse for å få automatiske varsler ved saksstatusendring eller nye meldinger. 
           <strong> Denne e-posten vises IKKE til saksbehandleren.</strong>
         </p>
         <input 
           type="email" 
           value={formData.notificationEmail} 
           onChange={e=> handleInputChange('notificationEmail', e.target.value)}
           placeholder="varsling@epost.no"
           className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-green-500 transition-all"
         />
       </motion.div>
     )}
   </div>
 </div>
 )}
 </div>
 </motion.div>
 )}

 {currentStep===6 && (
 <motion.div initial={{opacity: 0,scale: 0.98}} animate={{opacity: 1,scale: 1}} exit={{opacity: 0,scale: 0.98}} className="space-y-8">
 <div className="text-center mb-6">
 <h2 className="text-2xl font-black text-gray-900">Forhåndsvisning av varsel</h2>
 <p className="text-gray-600 text-sm">Vennligst se gjennom rapporten før du sender den inn.</p>
 </div>
 <div ref={reportRef} className="bg-white shadow-2xl rounded-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto flex flex-col font-serif min-h-[800px]">
 <div className="bg-blue-600 h-2 w-full"></div>
 <div className="p-12 space-y-10">
 <div className="flex justify-between items-start border-b-2 border-gray-900 pb-8">
 <div>
 <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase mb-2">Varslingsrapport</h1>
 <div className="flex items-center gap-4 text-sm text-gray-500 font-sans">
 <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} /> {new Date().toLocaleDateString('nb-NO')}</span>
 <span className="flex items-center gap-1 font-mono uppercase">ID: WH-PROT-{Date.now().toString().slice(-6)}</span>
 </div>
 </div>
 <div className="text-right font-sans">
 <div className="w-16 h-16 bg-gray-100 rounded-lg ml-auto mb-2 flex items-center justify-center text-gray-400">
 <SafeIcon icon={FiShield} size={32} />
 </div>
 <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Sikker Innsending</p>
 </div>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 font-sans">
 <section className="space-y-6">
 <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] border-b border-blue-100 pb-2">1. Om forholdet</h3>
 <div className="space-y-4">
 <PreviewField label="Tittel" value={formData.title} />
 <PreviewField label="Kategori" value={`${formData.category} - ${formData.type}`} />
 <PreviewField label="Alvorlighetsgrad" value={formData.severity.toUpperCase()} />
 <PreviewField label="Tidspunkt" value={new Date(formData.whenHappened).toLocaleString('nb-NO')} />
 <PreviewField label="Lokasjon" value={formData.whereHappened} />
 </div>
 </section>
 <section className="space-y-6">
 <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] border-b border-blue-100 pb-2">2. Involverte Parter</h3>
 <div className="space-y-4">
 <PreviewField label="Gjelder person(er)" value={formData.involvedPersons} />
 <PreviewField label="Eventuelle vitner" value={formData.witnesses} />
 </div>
 </section>
 <section className="md:col-span-2 space-y-6">
 <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] border-b border-blue-100 pb-2">3. Detaljert Beskrivelse</h3>
 <div className="bg-gray-50 p-6 rounded-lg text-gray-700 leading-relaxed italic border border-gray-100"> {formData.whatHappened} </div>
 </section>
 <section className="md:col-span-2 space-y-6">
 <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] border-b border-blue-100 pb-2">4. Dokumentasjon & Bevis</h3>
 <div className="grid md:grid-cols-2 gap-8">
 <PreviewField label="Beskrivelse av bevis" value={formData.evidenceDescription} />
 <div>
 <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Vedlegg ({formData.files.length})</p>
 <div className="flex flex-wrap gap-2"> {formData.files.map((f,i)=> ( <span key={i} className="text-[10px] bg-gray-100 px-2 py-1 rounded border border-gray-200">{f.name}</span> ))} </div>
 </div>
 </div>
 </section>
 <section className="md:col-span-2 space-y-6">
 <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] border-b border-blue-100 pb-2">5. Avsender</h3>
 <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-xl">
 {formData.isAnonymous ? (
 <div className="flex flex-col gap-2 w-full">
   <div className="flex items-center gap-3 text-blue-600">
    <SafeIcon icon={FiEyeOff} size={24} />
    <span className="font-black uppercase tracking-widest text-sm">Anonym Rapportering</span>
   </div>
   {formData.notificationsEnabled && (
     <p className="text-[10px] text-gray-400 italic">Automatiske varsler aktivert til: {formData.notificationEmail}</p>
   )}
 </div>
 ) : (
 <div className="grid grid-cols-3 gap-8 w-full">
 <PreviewField label="Navn" value={formData.reporterName} />
 <PreviewField label="E-post" value={formData.reporterEmail} />
 <PreviewField label="Telefon" value={formData.reporterPhone} />
 </div>
 )}
 </div>
 </section>
 </div>
 <div className="pt-12 mt-12 border-t border-gray-100 flex justify-between items-end">
 <div className="text-[10px] text-gray-400 font-sans space-y-1">
 <p>Dette dokumentet er generert i HMSVarsling-portalen.</p>
 <p>All data behandles konfidensielt i henhold til GDPR.</p>
 </div>
 <div className="text-right">
 <SafeIcon icon={FiCheckCircle} className="text-green-500 text-4xl ml-auto mb-2" />
 <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Klar for innsending</p>
 </div>
 </div>
 </div>
 </div>
 <div className="flex flex-wrap justify-between items-center max-w-4xl mx-auto gap-4">
 <div className="flex gap-3">
 <button type="button" onClick={()=> window.print()} className="flex items-center space-x-2 px-4 py-3 border-2 border-gray-800 rounded-xl text-gray-800 hover:bg-gray-100 transition-colors font-bold">
 <SafeIcon icon={FiPrinter} /> <span>Print</span>
 </button>
 <button type="button" onClick={handleDownloadPDF} disabled={isGenerating} className="flex items-center space-x-2 px-4 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-colors font-bold shadow-sm">
 {isGenerating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiDownload} />} <span>Last ned PDF</span>
 </button>
 <button type="button" onClick={handleShareEmail} className="flex items-center space-x-2 px-4 py-3 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors font-bold">
 <SafeIcon icon={FiMail} /> <span>E-post</span>
 </button>
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>

 <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center no-print">
 {currentStep > 1 ? (
 <button type="button" onClick={()=> setCurrentStep(prev=> prev - 1)} className="px-6 py-3 rounded-xl font-bold text-gray-600 hover:bg-gray-200 flex items-center gap-2">
 <SafeIcon icon={FiChevronLeft} /> Tilbake
 </button>
 ) : <div />}
 {currentStep < 6 ? (
 <button 
   type="button" 
   onClick={handleNext} 
   disabled={currentStep === 1 && !formData.type}
   className={`px-8 py-3 rounded-xl font-bold shadow-lg flex items-center gap-2 transition-all ${currentStep === 1 && !formData.type ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
 >
   Neste <SafeIcon icon={FiArrowRight} />
 </button>
 ) : (
 <button type="button" onClick={handleSubmit} disabled={isSubmitting} className={`px-10 py-4 bg-green-600 text-white rounded-xl font-black text-lg hover:bg-green-700 shadow-xl flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
 {isSubmitting ? (
 <>
 <SafeIcon icon={FiLoader} className="animate-spin" /> <span>Sender...</span>
 </>
 ) : (
 <>
 <span>Bekreft & Send rapport</span>
 <SafeIcon icon={FiSend} />
 </>
 )}
 </button>
 )}
 </div>
 </form>
 </div>
 <PortalFooter hideBranding={company?.whiteLabel} />
 </div>
);
};

export default WhistleblowerForm;